import React, { useMemo, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { TrendingUp, TrendingDown, AlertTriangle, FileText, ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const THEME_COLORS = {
  'Student dropped / not interested': 'bg-rose-500',
  'Batch shift / transfer / reschedule': 'bg-amber-500',
  'Financial issue / payment default': 'bg-orange-500',
  'Billing error / duplicate / wrong invoice': 'bg-indigo-500',
  'Course dissatisfaction / quality': 'bg-fuchsia-500',
  'Refund / cancellation as agreed': 'bg-emerald-500',
  'Other / unclear': 'bg-slate-400',
  'Unclassified': 'bg-slate-300',
};

export const CreditNoteExecutiveSummary = ({ exec, summary, rangeLabel, clientType, fmtInr, onOpenInvoice, onOpenTheme, open, onOpenChange }) => {

  const themes = exec?.reason_themes || [];
  const totalApplied = summary?.applied_total || 0;
  const topTheme = themes[0];
  const pop = exec?.pop;
  const worse = pop && pop.delta > 0;
  const salesOutliers = (exec?.accountability?.salesperson?.rows || []).filter((r) => r.outlier);
  const trainerOutliers = (exec?.accountability?.trainer?.rows || []).filter((r) => r.outlier);
  const full = exec?.watchlist?.full_writeoff || [];
  const partial = exec?.watchlist?.partial_writeoff || [];

  const actions = useMemo(() => {
    if (!exec) return [];
    const out = [];
    if (full.length) out.push({ tone: 'rose', text: `${full.length} invoice(s) were fully closed by a credit note with no collection (₹${Math.round(full.reduce((s, e) => s + e.cn_applied, 0)).toLocaleString('en-IN')}) — verify these were genuine, not masking non-collection.` });
    salesOutliers.forEach((r) => out.push({ tone: 'amber', text: `Salesperson ${r.name}: ${fmtInr(r.applied)} credited (${r.cn_count} CNs) — well above the ${fmtInr(exec.accountability.salesperson.avg_applied)} average. Review.` }));
    trainerOutliers.forEach((r) => out.push({ tone: 'amber', text: `Trainer ${r.name}: ${fmtInr(r.applied)} credited (${r.cn_count} CNs) — above the ${fmtInr(exec.accountability.trainer.avg_applied)} average. Review batch quality/retention.` }));
    const billing = themes.find((t) => t.theme.startsWith('Billing error'));
    if (billing && billing.applications) out.push({ tone: 'indigo', text: `${billing.applications} CN(s) tagged "Billing error / duplicate" (${fmtInr(billing.applied)}) — fixable process leakage; tighten invoicing.` });
    if (exec.unclassified) out.push({ tone: 'slate', text: `${exec.unclassified} CN application(s) have no theme yet — run "Classify reasons".` });
    if (topTheme) out.push({ tone: 'rose', text: `Biggest driver: "${topTheme.theme}" = ${fmtInr(topTheme.applied)} across ${topTheme.cn_count} CNs.` });
    return out;
  }, [exec, full, partial, salesOutliers, trainerOutliers, themes, topTheme, fmtInr]);

  if (!exec) return null;

  const exportPdf = () => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
    const pw = doc.internal.pageSize.getWidth();
    doc.setFillColor(190, 24, 93); doc.rect(0, 0, pw, 56, 'F');
    doc.setTextColor(255); doc.setFontSize(16); doc.setFont(undefined, 'bold');
    doc.text('Credit Notes — Executive Summary', 40, 26);
    doc.setFontSize(9); doc.setFont(undefined, 'normal');
    doc.text(`${rangeLabel} · Client: ${clientType === 'all' ? 'All types' : clientType} · Total leaked: INR ${Math.round(totalApplied).toLocaleString('en-IN')}`, 40, 44);
    let y = 74;
    doc.setTextColor(30); doc.setFontSize(11); doc.setFont(undefined, 'bold'); doc.text('Recommended actions', 40, y); y += 6;
    autoTable(doc, { startY: y, head: [['#', 'Action']], body: actions.map((a, i) => [i + 1, a.text]), styles: { fontSize: 8, cellPadding: 4, overflow: 'linebreak' }, headStyles: { fillColor: [30, 41, 59] }, columnStyles: { 0: { cellWidth: 24 }, 1: { cellWidth: pw - 104 } }, margin: { left: 40, right: 40 } });
    autoTable(doc, { startY: doc.lastAutoTable.finalY + 16, head: [['Reason theme', 'CNs', 'INR credited']], body: themes.map((t) => [t.theme, t.cn_count, Math.round(t.applied).toLocaleString('en-IN')]), styles: { fontSize: 8, cellPadding: 4 }, headStyles: { fillColor: [190, 24, 93] }, margin: { left: 40, right: 40 } });
    autoTable(doc, { startY: doc.lastAutoTable.finalY + 16, head: [['Salesperson', 'CNs', 'INR', 'Flag']], body: (exec.accountability?.salesperson?.rows || []).map((r) => [r.name, r.cn_count, Math.round(r.applied).toLocaleString('en-IN'), r.outlier ? 'HIGH' : '']), styles: { fontSize: 8, cellPadding: 4 }, headStyles: { fillColor: [124, 58, 237] }, margin: { left: 40, right: 40 } });
    doc.save('credit-notes-executive-summary.pdf');
  };

  const OutlierChips = ({ rows, label }) => (
    <div className="flex items-center gap-1.5 flex-wrap">
      <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</span>
      {rows.length === 0 ? <span className="text-[11px] text-slate-400">none flagged</span> : rows.map((r) => (
        <span key={r.name} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-100 text-amber-800 text-[11px] font-medium border border-amber-200">
          <AlertTriangle className="w-3 h-3" />{r.name} · {fmtInr(r.applied)}
        </span>
      ))}
    </div>
  );

  return (
    <>
      {/* Full report modal (trigger lives in the page header) */}
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="cn-exec-modal">
          <DialogHeader>
            <div className="flex items-center justify-between gap-2">
              <DialogTitle className="text-base">Credit Notes — Executive Summary</DialogTitle>
              <Button size="sm" variant="outline" onClick={exportPdf} className="h-7 text-[11px] mr-6" data-testid="cn-exec-pdf"><FileText className="w-3 h-3 mr-1" />PDF</Button>
            </div>
            <p className="text-[11px] text-slate-500">{rangeLabel} · {clientType === 'all' ? 'all client types' : clientType} · a credit note closes a receivable without collecting cash</p>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-4 pt-1">
            {/* Actions */}
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Recommended actions</h4>
              <div className="space-y-1.5">
                {actions.map((a, i) => (
                  <div key={i} className={`text-[12px] px-3 py-2 rounded-md border-l-4 ${a.tone === 'rose' ? 'border-rose-500 bg-rose-50' : a.tone === 'amber' ? 'border-amber-500 bg-amber-50' : a.tone === 'indigo' ? 'border-indigo-500 bg-indigo-50' : 'border-slate-400 bg-slate-50'}`} data-testid={`cn-exec-action-${i}`}>{a.text}</div>
                ))}
              </div>
            </div>
            {/* Themes */}
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Why it leaked (reason themes) <span className="text-slate-400 normal-case font-normal">— click a theme to see its cases</span></h4>
              <div className="space-y-1.5">
                {themes.map((t) => {
                  const pct = totalApplied ? (t.applied / totalApplied) * 100 : 0;
                  return (
                    <button
                      key={t.theme}
                      type="button"
                      onClick={() => onOpenTheme?.(t.theme)}
                      className="w-full flex items-center gap-2 text-[11px] px-1.5 py-1 -mx-1.5 rounded-md hover:bg-violet-50 transition-colors group cursor-pointer text-left"
                      title={`View the ${t.applications} application(s) tagged "${t.theme}"`}
                      data-testid={`cn-exec-theme-${t.theme.replace(/[^a-zA-Z]+/g, '-').toLowerCase()}`}
                    >
                      <span className="w-56 truncate text-slate-700 group-hover:text-violet-700 group-hover:underline">{t.theme}</span>
                      <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden"><div className={`h-full ${THEME_COLORS[t.theme] || 'bg-slate-400'} rounded-full`} style={{ width: `${pct}%` }} /></div>
                      <span className="w-10 text-right tabular-nums text-slate-500">{t.cn_count}</span>
                      <span className="w-20 text-right tabular-nums font-semibold text-rose-700">{fmtInr(t.applied)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            {/* Accountability */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[['Salesperson', 'salesperson'], ['Trainer', 'trainer']].map(([lbl, k]) => (
                <div key={k}>
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{lbl} accountability</h4>
                  <div className="space-y-1">
                    {(exec.accountability?.[k]?.rows || []).map((r) => (
                      <div key={r.name} className={`flex items-center gap-2 text-[11px] px-1.5 py-1 rounded ${r.outlier ? 'bg-amber-50' : ''}`}>
                        {r.outlier && <AlertTriangle className="w-3 h-3 text-amber-600 shrink-0" />}
                        <span className="flex-1 truncate text-slate-700">{r.name}</span>
                        <span className="text-slate-500 tabular-nums">{r.cn_count}</span>
                        <span className="w-20 text-right font-semibold tabular-nums text-rose-700">{fmtInr(r.applied)}</span>
                      </div>
                    ))}
                    <p className="text-[10px] text-slate-400 pt-0.5">avg {fmtInr(exec.accountability?.[k]?.avg_applied || 0)} · flagged if &gt; 1.75× avg</p>
                  </div>
                </div>
              ))}
            </div>
            {/* Watchlists */}
            {[['Closed WITHOUT payment (100% written off by CN)', full, 'rose'], ['Partial write-off (40–99% credited)', partial, 'amber']].map(([title, list, tone]) => (
              <div key={title}>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{title} <span className="text-slate-400 normal-case">({list.length})</span></h4>
                <div className="border border-slate-200 rounded-lg overflow-hidden max-h-56 overflow-y-auto">
                  {list.length === 0 ? <p className="text-[11px] text-slate-400 py-4 text-center">None.</p> : list.map((e) => (
                    <div key={e.invoice_id} onClick={() => onOpenInvoice?.(e.invoice_id)} className="grid grid-cols-12 gap-2 px-2.5 py-1.5 border-b border-slate-100 text-[11px] items-center hover:bg-slate-50 cursor-pointer" data-testid={`cn-exec-wl-${e.invoice_id}`}>
                      <span className="col-span-2 font-medium text-violet-700 truncate">{e.invoice_number}</span>
                      <span className="col-span-3 truncate text-slate-700" title={e.customer_name}>{e.customer_name}</span>
                      <span className="col-span-2 truncate text-slate-500" title={e.salesperson}>{e.salesperson}</span>
                      <span className="col-span-3 truncate text-slate-400" title={e.cn_reason}>{e.reason_theme || e.cn_reason}</span>
                      <span className={`col-span-1 text-right tabular-nums font-bold ${tone === 'rose' ? 'text-rose-700' : 'text-amber-700'}`}>{e.writeoff_pct}%</span>
                      <span className="col-span-1 text-right tabular-nums font-semibold text-rose-700">{fmtInr(e.cn_applied)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CreditNoteExecutiveSummary;
